import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import StoryCard from '../components/StoryCard'
import QuizCard from '../components/QuizCard'
import ReportModal from '../components/ReportModal'
import { useAuth, displayUser, isTeenAccount } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { sanitizeBio } from '../utils/renderBio'
import { decodeEntities } from '../lib/decodeEntities'
import { supabase } from '../lib/supabase'
import SEO from '../components/SEO'
import SkeletonLoader from '../components/SkeletonLoader'
import BackToTop from '../components/BackToTop'
import OnlineDot from '../components/OnlineDot'
import { WallPost } from '../components/WallPost'
import { useOnlineStatus } from '../context/OnlineStatusContext'

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return session ? { Authorization: `Bearer ${session.access_token}` } : {}
}

function formatNum(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
  if (n >= 1000)    return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

const TABS = ['Stories', 'Quizzes', 'Announcements', 'Wall', 'Saved', 'Followers', 'Following', 'About']

// Detect if a CSS background value is dark (for readable tab text)
function isDarkBg(bg) {
  if (!bg) return false
  // Parse every hex color in the value (gradients carry multiple stops).
  const matches = [...bg.matchAll(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g)]
  if (matches.length === 0) return false
  const luminances = matches.map(m => {
    const h = m[1]
    const c = h.length === 3
      ? h.split('').map(d => parseInt(d + d, 16))
      : [h.slice(0,2), h.slice(2,4), h.slice(4,6)].map(d => parseInt(d, 16))
    return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255
  })
  const avg = luminances.reduce((s, l) => s + l, 0) / luminances.length
  return avg < 0.45
}

// Simple in-memory cache so navigating away and back doesn't flash a white loading screen
const _profileCache = {}

// Convert API profile shape into the same shape the UI expects
function apiToUiUser(profile) {
  return {
    username:       profile.username,
    displayName:    profile.displayName,
    tagline:        profile.tagline,
    pronouns:       profile.pronouns,
    bio:            profile.bio,
    avatar:         profile.displayName?.[0]?.toUpperCase() ?? '?',
    avatarGrad:     profile.avatarGrad,
    bannerGrad:     profile.bannerGrad,
    avatarUrl:      profile.avatarUrl ?? null,
    bannerUrl:      profile.bannerUrl ?? null,
    profileBg:      profile.profileBg,
    joined:         profile.joinedAt,
    location:       profile.location,
    website:        profile.website,
    followers:      profile.followers,
    following:      profile.following,
    fandoms:        profile.fandoms ?? [],
    role:           profile.role ?? null,
    legacy:         profile.legacy ?? false,
    isMinor:        profile.isMinor ?? false,
    // These come from story aggregation
    totalLikes:     0,
    genres:         [],
    favoriteAuthors: [],
    writingSchedule: null,
  }
}

export default function UserProfile() {
  const { username }   = useParams()
  const navigate       = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, profile: authProfile } = useAuth()
  const { theme } = useTheme()
  const { isOnline }   = useOnlineStatus()
  // PR 2: pass authProfile so displayUser reads profile.username (server-authoritative)
  const currentUser    = displayUser(user, authProfile)

  const [profileData, setProfileData] = useState(_profileCache[username] ?? null)
  const [loadError, setLoadError]     = useState(false)

  const rawTab = searchParams.get('tab') ?? 'stories'
  const tab = TABS.find(t => t.toLowerCase() === rawTab.toLowerCase()) ?? 'Stories'

  function setTab(t) {
    setSearchParams({ tab: t.toLowerCase() }, { replace: true })
  }
  const [following, setFollowing]         = useState(false)
  const [requested, setRequested]         = useState(false)
  const [requestId, setRequestId]         = useState(null)
  const [followCount, setFollowCount]     = useState(null)  // null = not yet loaded
  const [followingCount, setFollowingCount] = useState(null)
  const [followBusy, setFollowBusy]       = useState(false)
  const [quizCount, setQuizCount]         = useState(null)

  const [blocking, setBlocking]   = useState(false)
  const [blockedBy, setBlockedBy] = useState(false)
  const [blockBusy, setBlockBusy] = useState(false)
  const [reportingUser, setReportingUser] = useState(false)

  useEffect(() => {
    let cancelled = false

    setLoadError(false)

    // Keep cached data visible while refetching; only clear for a new user
    if (!_profileCache[username]) {
      setProfileData(null)
    }
    setFollowing(false)
    setFollowCount(_profileCache[username]?.profile.followers ?? null)
    setFollowingCount(_profileCache[username]?.profile.following ?? null)
    setQuizCount(null)

    getHeaders().then(headers => Promise.all([
      axios.get(`/api/profile/${username}`, { headers }),
      axios.get(`/api/profile/${username}/quizzes`, { headers }).catch(() => ({ data: [] })),
    ])).then(([profileRes, quizzesRes]) => {
      if (cancelled) return
      _profileCache[username] = profileRes.data
      setProfileData(profileRes.data)
      setFollowCount(profileRes.data.profile.followers)
      setFollowingCount(profileRes.data.profile.following)
      setQuizCount((quizzesRes.data ?? []).length)
    }).catch(() => {
      if (cancelled) return
      setLoadError(true)
    })

    return () => { cancelled = true }
  }, [username])

  // Check follow status once we know the current user and the profile loaded.
  // Response shape extended in Commit 2: { following, requested, requestId }.
  useEffect(() => {
    if (!user || !profileData || isOwnProfileCheck()) return
    getHeaders().then(headers =>
      axios.get(`/api/follows/status/${username}`, { headers })
        .then(res => {
          setFollowing(res.data.following)
          setRequested(res.data.requested ?? false)
          setRequestId(res.data.requestId ?? null)
        })
        .catch(err => console.error('[UserProfile] Failed to fetch follow status:', err))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profileData])

  // Check block status
  useEffect(() => {
    if (!user || !profileData || isOwnProfileCheck()) return
    getHeaders().then(headers =>
      axios.get(`/api/blocks/status/${username}`, { headers })
        .then(res => { setBlocking(res.data.blocking); setBlockedBy(res.data.blockedBy) })
        .catch(err => console.error('[UserProfile] Failed to fetch block status:', err))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profileData])

  async function handleBlockToggle() {
    if (blockBusy) return
    setBlockBusy(true)
    try {
      const headers = await getHeaders()
      if (blocking) {
        await axios.delete(`/api/blocks/${username}`, { headers })
        setBlocking(false)
      } else {
        await axios.post(`/api/blocks/${username}`, {}, { headers })
        setBlocking(true)
        setFollowing(false)
      }
    } catch (err) { console.error('[UserProfile] Failed to toggle block status:', err) } finally {
      setBlockBusy(false)
    }
  }

  function isOwnProfileCheck() {
    const cu = displayUser(user, authProfile)
    return cu?.username === username
  }

  async function handleFollowToggle() {
    if (followBusy) return
    setFollowBusy(true)
    const wasFollowing = following
    const wasRequested = requested
    const oldRequestId = requestId

    try {
      const headers = await getHeaders()

      if (wasFollowing) {
        // Unfollow: optimistic decrement, then DELETE
        setFollowing(false)
        setFollowCount(c => Math.max(0, (c ?? 1) - 1))
        await axios.delete(`/api/follows/${username}`, { headers })
      } else if (wasRequested) {
        // Cancel pending request: optimistic clear, then DELETE the request
        setRequested(false)
        setRequestId(null)
        if (oldRequestId) {
          await axios.delete(`/api/follow-requests/${oldRequestId}`, { headers })
        }
      } else {
        // Fresh follow attempt — server decides follows vs follow_requests
        // based on target.privacy_mode. Response shape:
        //   { following: true }                          → privacy_mode=false
        //   { requested: true, requestId: <uuid> }       → privacy_mode=true
        const res = await axios.post(`/api/follows/${username}`, {}, { headers })
        if (res.data.requested) {
          setRequested(true)
          setRequestId(res.data.requestId ?? null)
        } else {
          setFollowing(true)
          setFollowCount(c => (c ?? 0) + 1)
        }
      }
    } catch (err) {
      // Revert on error
      setFollowing(wasFollowing)
      setRequested(wasRequested)
      setRequestId(oldRequestId)
      setFollowCount(c => wasFollowing ? (c ?? 0) + 1 : c)
      console.error('[follow] toggle failed:', JSON.stringify(err?.response?.data), 'status:', err?.response?.status)
    } finally {
      setFollowBusy(false)
    }
  }

  const isAdmin = authProfile?.isAdmin === true

  const uiUser      = profileData ? apiToUiUser(profileData.profile) : null
  const stories     = profileData?.stories ?? []
  const pinnedStory = profileData?.pinnedStory ?? null
  const totalWords = stories.reduce((s, x) => s + (x.words ?? 0), 0)
  const totalLikes = stories.reduce((s, x) => s + (x.likes ?? 0), 0)

  const isOwnProfile     = currentUser?.username === username
  const displayFollowers = followCount  ?? (profileData ? 0 : null)
  const displayFollowing = followingCount ?? (profileData ? 0 : null)

  // When the viewer isn't the profile owner and the list is gated by privacy,
  // the counter and the tab content should agree — mask the number and show a
  // "private" message instead of the empty state. Mirrors the server-side
  // privacy gate at /api/follows/:username/{followers,following}.
  const followersListPrivate = !isOwnProfile && profileData?.canSeeFollowers === false
  const followingListPrivate = !isOwnProfile && profileData?.canSeeFollowing === false

  if (loadError && !profileData) return (
    <div className="profile-page">
      <Navbar />
      <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--text-faint)' }}>
        <div style={{ fontSize: '2rem', marginBottom: '12px' }}>👤</div>
        <h2 style={{ marginBottom: '8px' }}>User not found</h2>
        <p>This profile doesn't exist or may have been deleted.</p>
      </div>
    </div>
  )

  if (!uiUser) return (
    <div className="profile-page">
      <Navbar />
      <SkeletonLoader type="profile" />
    </div>
  )

  // Suppress user-set profileBg when it conflicts with the active theme:
  // a light bg in dark theme makes near-white text invisible. High-contrast
  // theme always wins (accessibility takes priority over decoration).
  const themeIsDark = theme === 'dark' || theme === 'high-contrast'
  const userBgIsDark = isDarkBg(uiUser.profileBg)
  const suppressUserBg = themeIsDark && (theme === 'high-contrast' || !userBgIsDark)
  const effectiveBg = suppressUserBg ? null : uiUser.profileBg

  const pageStyle = effectiveBg
    ? { background: effectiveBg, minHeight: '100vh', '--profile-page-bg': effectiveBg }
    : {}
  const darkBg = !!effectiveBg && userBgIsDark

  return (
    <div className={`profile-page${darkBg ? ' profile-page--dark-bg' : ''}`} style={pageStyle}>
      <SEO
        title={decodeEntities(uiUser.displayName || uiUser.username)}
        description={decodeEntities(uiUser.tagline || `${uiUser.displayName || uiUser.username}'s profile on Verse — stories, quizzes, and more.`)}
        image={uiUser.avatarUrl}
        type="profile"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ProfilePage',
          mainEntity: {
            '@type': 'Person',
            name: uiUser.displayName || uiUser.username,
            url: `https://versesite.net/user/${uiUser.username}`,
          },
        }}
      />
      <Navbar />

      {/* ── Banner ── */}
      <div className="profile-banner" style={{ background: uiUser.bannerUrl ? undefined : uiUser.bannerGrad }}>
        {uiUser.bannerUrl
          ? <img src={uiUser.bannerUrl} alt={`${uiUser.displayName || uiUser.username}'s profile banner`} className="profile-banner__img" />
          : <><div className="profile-banner__blob profile-banner__blob--1" /><div className="profile-banner__blob profile-banner__blob--2" /></>
        }
      </div>

      {/* ── Header ── */}
      <div className="profile-header-wrap">
        <div className="profile-header">
          <div className="profile-header__left">
            <div className="profile-avatar" style={{ position: 'relative', background: uiUser.avatarUrl ? undefined : uiUser.avatarGrad }}>
              {uiUser.avatarUrl
                ? <img src={uiUser.avatarUrl} alt={`${uiUser.displayName || uiUser.username}`} className="profile-avatar__img" />
                : uiUser.avatar
              }
            </div>

            <div className="profile-header__info">
              <h1 className="profile-displayname" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {decodeEntities(uiUser.displayName)}
                {uiUser.role === 'admin' && <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--tag-rating-bg)', color: 'var(--tag-rating-text)', fontSize: '0.7rem', fontWeight: 600 }}>🛡️ Admin</span>}
                {uiUser.role === 'moderator' && <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--tag-genre-bg)', color: 'var(--tag-genre-text)', fontSize: '0.7rem', fontWeight: 600 }}>Moderator</span>}
                {uiUser.legacy && <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--tag-trope-bg)', color: 'var(--tag-trope-text)', fontSize: '0.7rem', fontWeight: 600 }}>Legacy</span>}
              </h1>

              <div className="profile-username-row">
                <div className="profile-username">@{uiUser.username}</div>
                {uiUser.pronouns && (
                  <span className="profile-pronouns">{decodeEntities(uiUser.pronouns)}</span>
                )}
                {profileData?.profile?.showOnlineStatus !== false && (isOnline(uiUser.id) || profileData?.profile?.isOnline) && (
                  <span style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: 600 }}>● Online</span>
                )}
              </div>

              {uiUser.tagline && (
                <div className="profile-tagline">{decodeEntities(uiUser.tagline)}</div>
              )}

              {(uiUser.location || uiUser.joined) && (
                <div className="profile-meta">
                  {uiUser.location && <span className="profile-meta__item">📍 {decodeEntities(uiUser.location)}</span>}
                  {uiUser.joined   && <span className="profile-meta__item">📅 Joined {formatDate(uiUser.joined)}</span>}
                  {uiUser.website && (
                    <a href={uiUser.website} target="_blank" rel="noopener noreferrer" className="profile-meta__item profile-meta__link">
                      🔗 {uiUser.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="profile-header__actions">
            {isOwnProfile ? (
              <button
                className="btn btn-outline profile-action-btn"
                onClick={() => navigate('/settings/profile')}
              >
                Edit profile
              </button>
            ) : (
              <>
                {user && !blocking && !blockedBy && (() => {
                  // Three-state follow button. Initial label uses
                  // requiresFollowRequest from the public-profile response
                  // (normaliseProfilePublic derived boolean) so the user
                  // sees "Request to follow" up front for protected accounts,
                  // not "Follow → Requested" flip after click.
                  let label
                  if (followBusy)         label = '…'
                  else if (following)     label = '✓ Following'
                  else if (requested)     label = '✓ Requested'
                  else if (profileData?.profile?.requiresFollowRequest) label = '+ Request to follow'
                  else                    label = '+ Follow'
                  const isInactive = following || requested
                  return (
                    <button
                      className={`btn profile-action-btn${isInactive ? ' btn-outline profile-action-btn--following' : ' btn-primary'}`}
                      onClick={handleFollowToggle}
                      disabled={followBusy}
                    >
                      {label}
                    </button>
                  )
                })()}
                {!blocking && !blockedBy && !isTeenAccount(authProfile) && profileData?.canMessage !== false && (
                  <button
                    className="btn btn-outline profile-action-btn"
                    onClick={() => navigate(`/messages?with=${encodeURIComponent(uiUser.username)}`)}
                  >
                    Message
                  </button>
                )}
                {user && (
                  <button
                    className="btn btn-outline profile-action-btn profile-action-btn--block"
                    onClick={handleBlockToggle}
                    disabled={blockBusy}
                  >
                    {blockBusy ? '…' : blocking ? 'Unblock' : 'Block'}
                  </button>
                )}
                {user && (
                  <button
                    className="btn btn-outline profile-action-btn profile-action-btn--report"
                    onClick={() => setReportingUser(true)}
                  >
                    Report
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Admin tools ── */}
        {isAdmin && !isOwnProfile && profileData?.profile?.id && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, padding: '0 24px', margin: '8px 0' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <AdminHideButton type="profile" id={profileData.profile.id} hidden={profileData.profile.hidden} onToggle={(h) => {
                setProfileData(prev => ({ ...prev, profile: { ...prev.profile, hidden: h } }))
              }} />
              <AdminWarningTool userId={profileData.profile.id} onIssued={() => {}} />
              <AdminSuspendTool userId={profileData.profile.id} />
              <AdminRestrictTool userId={profileData.profile.id} restricted={profileData.profile.restricted} onToggle={(r) => {
                setProfileData(prev => ({ ...prev, profile: { ...prev.profile, restricted: r } }))
              }} />
            </div>
          </div>
        )}

        {/* ── Stats bar ── */}
        <div className="profile-stats">
          <StatPill value={formatNum(stories.length)} label="Stories"       color="var(--purple)" />
          <StatPill value={formatNum(totalWords)}      label="Words written" color="var(--pink)" />
          <StatPill value={formatNum(totalLikes)}      label="Total likes"   color="#EC4899" />
          <StatPill value={followersListPrivate ? '—' : (displayFollowers !== null ? formatNum(displayFollowers) : '—')} label="Followers" color="var(--teal)"  onClick={() => setTab('Followers')} />
          <StatPill value={followingListPrivate ? '—' : (displayFollowing !== null ? formatNum(displayFollowing) : '—')} label="Following" color="#1D4ED8"     onClick={() => setTab('Following')} />
        </div>

        {/* ── Tabs ── */}
        <div className="profile-tabs" role="tablist" aria-label="Profile sections">
          {TABS.filter(t => t !== 'Wall' || profileData?.profile?.showWall !== false || isOwnProfile).map(t => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              className={`profile-tab${tab === t ? ' profile-tab--active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t}
              {t === 'Stories'   && <span className="profile-tab__badge">{stories.length}</span>}
              {t === 'Quizzes'   && quizCount > 0 && <span className="profile-tab__badge">{quizCount}</span>}
              {false /* favorites count loaded inside tab */}
              {t === 'Followers' && !followersListPrivate && displayFollowers > 0 && <span className="profile-tab__badge">{formatNum(displayFollowers)}</span>}
              {t === 'Following' && !followingListPrivate && displayFollowing > 0 && <span className="profile-tab__badge">{formatNum(displayFollowing)}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab body ── */}
      <div className="profile-body" role="tabpanel" aria-label={tab}>
        {blocking && (
          <div className="profile-blocked-notice">
            <div className="profile-blocked-notice__icon">🚫</div>
            <p className="profile-blocked-notice__text">You have blocked this user. Their content is hidden.</p>
            <button className="btn btn-outline" onClick={handleBlockToggle} disabled={blockBusy}>Unblock</button>
          </div>
        )}
        {!blocking && blockedBy && (
          <div className="profile-blocked-notice">
            <div className="profile-blocked-notice__icon">🚫</div>
            <p className="profile-blocked-notice__text">You cannot view this profile.</p>
          </div>
        )}
        {!blocking && !blockedBy && tab === 'Stories'       && <StoriesTab key={username} stories={stories} username={uiUser.username} isOwnProfile={isOwnProfile} pinnedStory={pinnedStory} />}
        {!blocking && !blockedBy && tab === 'Quizzes'       && <QuizzesTab username={username} isOwnProfile={isOwnProfile} currentUserId={user?.id} />}
        {!blocking && !blockedBy && tab === 'Announcements' && <AnnouncementsTab username={username} isOwnProfile={isOwnProfile} currentUser={currentUser} />}
        {!blocking && !blockedBy && tab === 'Wall'          && <WallTab username={username} isOwnProfile={isOwnProfile} isAdmin={isAdmin} />}
        {!blocking && !blockedBy && tab === 'Saved'         && <FavoritesTab username={username} isOwnProfile={isOwnProfile} />}
        {!blocking && !blockedBy && tab === 'Followers'     && <FollowListTab username={username} type="followers" currentUser={currentUser} isPrivate={followersListPrivate} />}
        {!blocking && !blockedBy && tab === 'Following'     && <FollowListTab username={username} type="following" currentUser={currentUser} isPrivate={followingListPrivate} />}
        {!blocking && !blockedBy && tab === 'About'         && <AboutTab user={uiUser} stories={stories} totalWords={totalWords} />}
      </div>
      {reportingUser && profileData?.profile?.id && (
        <ReportModal
          contentType="profile"
          contentId={profileData.profile.id}
          contentTitle={uiUser?.displayName ?? username}
          onClose={() => setReportingUser(false)}
        />
      )}
      <BackToTop />
    </div>
  )
}

// ─── Tabs ──────────────────────────────────────────────────────────────────────

function StoriesTab({ stories: initialStories, username, isOwnProfile, pinnedStory }) {
  const [stories, setStories] = useState(initialStories)

  async function handleDelete(storyId) {
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/stories/${storyId}`, { headers })
      setStories(ss => ss.filter(s => s.id !== storyId))
    } catch (err) { console.error('[UserProfile] Failed to delete story:', err) }
  }

  const published = stories.filter(s => s.published !== false)
  const drafts    = stories.filter(s => s.published === false)

  if (stories.length === 0) {
    return (
      <div className="profile-empty">
        <div className="profile-empty__icon">✍️</div>
        <p className="profile-empty__title">No stories yet</p>
        <p className="profile-empty__desc">
          {isOwnProfile
            ? "You haven't published any stories yet. Ready to start writing?"
            : `${username} hasn't published any stories yet.`}
        </p>
        {isOwnProfile && (
          <Link to="/write" className="btn btn-primary" style={{ marginTop: 16 }}>
            Start a story
          </Link>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      {pinnedStory && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>📌 Featured</div>
          <div style={{ borderRadius: 12, overflow: 'hidden' }}>
            <StoryCard story={pinnedStory} index={0} />
          </div>
        </div>
      )}

      {published.length > 0 && (
        <div>
          {isOwnProfile && (
            <div className="profile-stories-header">
              <div className="profile-stories-count">{published.length} published {published.length === 1 ? 'story' : 'stories'}</div>
              <Link to="/write" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 18px' }}>
                ✍️ New story
              </Link>
            </div>
          )}
          <div className="story-grid">
            {published.map((story, i) => (
              <StoryCard
                key={story.id}
                story={story}
                index={i}
                onDelete={isOwnProfile ? handleDelete : null}
              />
            ))}
          </div>
        </div>
      )}

      {isOwnProfile && drafts.length > 0 && (
        <div>
          <div className="profile-stories-header">
            <div className="profile-stories-count" style={{ color: 'var(--text-faint)' }}>{drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'}</div>
            {published.length === 0 && (
              <Link to="/write" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 18px' }}>
                ✍️ New story
              </Link>
            )}
          </div>
          <div className="story-grid">
            {drafts.map((story, i) => (
              <StoryCard
                key={story.id}
                story={story}
                index={published.length + i}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      {isOwnProfile && published.length === 0 && drafts.length === 0 && (
        <div className="profile-stories-header" style={{ justifyContent: 'flex-end' }}>
          <Link to="/write" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 18px' }}>
            ✍️ New story
          </Link>
        </div>
      )}
    </div>
  )
}

function QuizzesTab({ username, isOwnProfile, currentUserId }) {
  const [quizzes, setQuizzes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getHeaders().then(headers =>
      axios.get(`/api/profile/${username}/quizzes`, { headers })
        .then(r => setQuizzes(r.data ?? []))
        .catch(err => console.error('[UserProfile] Failed to fetch quizzes:', err))
        .finally(() => setLoading(false))
    )
  }, [username])

  async function handleDelete(id) {
    const headers = await getHeaders()
    await axios.delete(`/api/quizzes/${id}`, { headers })
    setQuizzes(prev => prev.filter(q => q.id !== id))
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-faint)' }}>Loading…</div>
  }

  if (quizzes.length === 0) {
    return (
      <div className="profile-empty">
        <div className="profile-empty__icon">🧠</div>
        <p className="profile-empty__title">No quizzes yet</p>
        <p className="profile-empty__desc">
          {isOwnProfile
            ? "You haven't created any quizzes yet. Ready to make one?"
            : `${username} hasn't published any quizzes yet.`}
        </p>
        {isOwnProfile && (
          <Link to="/quiz/create" className="btn btn-primary" style={{ marginTop: 16 }}>
            Create a quiz
          </Link>
        )}
      </div>
    )
  }

  const published = quizzes.filter(q => q.published)
  const drafts    = quizzes.filter(q => !q.published)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      {published.length > 0 && (
        <div>
          {isOwnProfile && (
            <div className="profile-stories-header">
              <div className="profile-stories-count">{published.length} published {published.length === 1 ? 'quiz' : 'quizzes'}</div>
              <Link to="/quiz/create" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 18px' }}>
                🧠 New quiz
              </Link>
            </div>
          )}
          <div className="quiz-grid">
            {published.map((quiz, i) => (
              <QuizCard key={quiz.id} quiz={quiz} index={i} currentUserId={currentUserId} onDelete={handleDelete} />
            ))}
          </div>
        </div>
      )}

      {isOwnProfile && drafts.length > 0 && (
        <div>
          <div className="profile-stories-header">
            <div className="profile-stories-count" style={{ color: 'var(--text-faint)' }}>{drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'}</div>
          </div>
          <div className="quiz-grid">
            {drafts.map((quiz, i) => (
              <QuizCard key={quiz.id} quiz={quiz} index={published.length + i} currentUserId={currentUserId} onDelete={handleDelete} />
            ))}
          </div>
        </div>
      )}

      {isOwnProfile && published.length === 0 && (
        <div className="profile-stories-header" style={{ justifyContent: 'flex-end' }}>
          <Link to="/quiz/create" className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '8px 18px' }}>
            🧠 New quiz
          </Link>
        </div>
      )}
    </div>
  )
}

function FavoritesTab({ username, isOwnProfile }) {
  const [stories, setStories] = useState([])
  const [quizzes, setQuizzes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isOwnProfile) { setLoading(false); return }
    getHeaders().then(headers => Promise.all([
      axios.get(`/api/profile/${username}/bookmarks`,      { headers }).then(r => setStories(r.data ?? [])).catch(err => console.error('[UserProfile] Failed to fetch story bookmarks:', err)),
      axios.get(`/api/profile/${username}/quiz-bookmarks`, { headers }).then(r => setQuizzes(r.data ?? [])).catch(err => console.error('[UserProfile] Failed to fetch quiz bookmarks:', err)),
    ])).finally(() => setLoading(false))
  }, [username, isOwnProfile])

  if (!isOwnProfile) {
    return (
      <div className="profile-empty">
        <div className="profile-empty__icon">🔖</div>
        <p className="profile-empty__title">Bookmarks are private</p>
        <p className="profile-empty__desc">Only the account owner can see their saved items.</p>
      </div>
    )
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-faint)' }}>Loading…</div>
  }

  if (stories.length === 0 && quizzes.length === 0) {
    return (
      <div className="profile-empty">
        <div className="profile-empty__icon">🔖</div>
        <p className="profile-empty__title">Nothing saved yet</p>
        <p className="profile-empty__desc">Stories and quizzes you save will appear here.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      {stories.length > 0 && (
        <div>
          <div className="profile-stories-header">
            <div className="profile-stories-count">{stories.length} saved {stories.length === 1 ? 'story' : 'stories'}</div>
          </div>
          <div className="story-grid">
            {stories.map((story, i) => (
              <StoryCard key={story.id} story={story} index={i + 2} />
            ))}
          </div>
        </div>
      )}

      {quizzes.length > 0 && (
        <div>
          <div className="profile-stories-header">
            <div className="profile-stories-count">{quizzes.length} saved {quizzes.length === 1 ? 'quiz' : 'quizzes'}</div>
          </div>
          <div className="quiz-grid">
            {quizzes.map((quiz, i) => (
              <QuizCard key={quiz.id} quiz={quiz} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function AboutTab({ user, stories, totalWords }) {
  const avgWords = stories.length ? Math.round(totalWords / stories.length) : 0
  const topStory = [...stories].sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0))[0]

  return (
    <div className="about-tab">
      <div className="about-tab__main">
        {/* Bio */}
        {user.bio && (
          <div className="about-card">
            <h3 className="about-card__title">Bio</h3>
            <div
              className="about-bio-text"
              dangerouslySetInnerHTML={{ __html: sanitizeBio(user.bio) }}
            />
          </div>
        )}

        {/* Fandoms */}
        {user.fandoms?.length > 0 && (() => {
          const storyCountByFandom = {}
          const topStoryByFandom = {}
          stories.forEach(s => {
            if (!s.fandom) return
            storyCountByFandom[s.fandom] = (storyCountByFandom[s.fandom] ?? 0) + 1
            if (!topStoryByFandom[s.fandom] || s.likes > topStoryByFandom[s.fandom].likes) {
              topStoryByFandom[s.fandom] = s
            }
          })
          const fandomsWithStories = user.fandoms.filter(f => storyCountByFandom[f])
          return (
            <div className="about-card">
              <h3 className="about-card__title">Fandoms</h3>
              <div className="about-fandom-list">
                {user.fandoms.map(f => (
                  <span key={f} className="about-fandom-chip">
                    {f}
                    {storyCountByFandom[f] && (
                      <span style={{
                        marginLeft: 6,
                        background: 'var(--purple)',
                        color: '#fff',
                        borderRadius: 99,
                        fontSize: '0.7rem',
                        padding: '1px 7px',
                        fontWeight: 700,
                      }}>
                        {storyCountByFandom[f]}
                      </span>
                    )}
                  </span>
                ))}
              </div>

              {fandomsWithStories.length > 0 && (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {fandomsWithStories.map(f => {
                    const top = topStoryByFandom[f]
                    const count = storyCountByFandom[f]
                    return (
                      <Link
                        key={f}
                        to={`/story/${top.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '10px 14px',
                          background: 'var(--surface)',
                          borderRadius: 10,
                          border: '1.5px solid var(--border)',
                          textDecoration: 'none',
                          color: 'inherit',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text)', marginBottom: 2 }}>
                            {decodeEntities(top.title)}
                          </div>
                          <div style={{ fontSize: '0.775rem', color: 'var(--text-faint)' }}>
                            {f} · {count} {count === 1 ? 'story' : 'stories'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, fontSize: '0.775rem', color: 'var(--text-faint)', flexShrink: 0 }}>
                          {top.likes > 0 && <span>❤️ {formatNum(top.likes)}</span>}
                          {top.words > 0 && <span>📖 {formatNum(top.words)}w</span>}
                        </div>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })()}

        {/* Genres (derived from stories) */}
        {stories.length > 0 && (() => {
          const genreCount = {}
          stories.forEach(s => (s.genre ?? []).forEach(g => { genreCount[g] = (genreCount[g] ?? 0) + 1 }))
          const topGenres = Object.entries(genreCount).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([g]) => g)
          if (!topGenres.length) return null
          const colors = ['var(--purple)', 'var(--pink)', 'var(--teal)', '#EA580C', '#1D4ED8', '#BE123C']
          return (
            <div className="about-card">
              <h3 className="about-card__title">Genres I write</h3>
              <div className="about-fandom-list">
                {topGenres.map((g, i) => {
                  const color = colors[i % colors.length]
                  return (
                    <span
                      key={g}
                      className="about-fandom-chip"
                      style={{ background: color + '18', color, border: `1.5px solid ${color}33` }}
                    >
                      {g}
                    </span>
                  )
                })}
              </div>
            </div>
          )
        })()}
      </div>

      <div className="about-tab__side">
        {/* Writing stats */}
        <div className="about-stats-card">
          <h3 className="about-card__title">Writing stats</h3>
          <div className="about-stat-list">
            <AboutStat label="Total stories"   value={stories.length} />
            <AboutStat label="Total words"     value={formatNum(totalWords)} />
            <AboutStat label="Avg words/story" value={formatNum(avgWords)} />
            <AboutStat label="Followers"       value={formatNum(user.followers)} />
            {user.joined && <AboutStat label="Member since" value={formatDate(user.joined)} />}
          </div>
        </div>

        {/* Top story */}
        {topStory && (
          <div className="about-top-story">
            <h3 className="about-card__title">Most popular</h3>
            <Link to={`/story/${topStory.id}`} className="about-top-story__card">
              <div className="about-top-story__cover" style={{ background: user.bannerGrad }} />
              <div className="about-top-story__info">
                <div className="about-top-story__title">{decodeEntities(topStory.title)}</div>
                <div className="about-top-story__meta">
                  ❤️ {formatNum(topStory.likes)} · 📖 {topStory.chapters} ch
                </div>
              </div>
            </Link>
          </div>
        )}

      </div>
    </div>
  )
}

// ─── Tiny helpers ──────────────────────────────────────────────────────────────

function StatPill({ value, label, color, onClick }) {
  if (onClick) {
    return (
      <button className="profile-stat profile-stat--clickable" onClick={onClick}>
        <span className="profile-stat__value" style={{ color }}>{value}</span>
        <span className="profile-stat__label">{label}</span>
      </button>
    )
  }
  return (
    <div className="profile-stat">
      <span className="profile-stat__value" style={{ color }}>{value}</span>
      <span className="profile-stat__label">{label}</span>
    </div>
  )
}

// ─── Follow list tab ────────────────────────────────────────────────────────

function FollowListTab({ username, type, currentUser, isPrivate = false }) {
  const PAGE_SIZE = 50
  const [people, setPeople]           = useState([])
  const [loading, setLoading]         = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore]         = useState(true)
  const [nextOffset, setNextOffset]   = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setPeople([])
    setHasMore(true)
    setNextOffset(0)
    getHeaders()
      .then(headers => axios.get(
        `/api/follows/${username}/${type}?limit=${PAGE_SIZE}&offset=0`,
        { headers }
      ))
      .then(res => {
        if (cancelled) return
        setPeople(res.data.people)
        setHasMore(res.data.hasMore)
        setNextOffset(res.data.nextOffset)
      })
      .catch(err => {
        if (!cancelled) console.error('[UserProfile] Failed to fetch follow list:', err)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [username, type])

  async function loadMore() {
    setLoadingMore(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.get(
        `/api/follows/${username}/${type}?limit=${PAGE_SIZE}&offset=${nextOffset}`,
        { headers }
      )
      setPeople(prev => [...prev, ...data.people])
      setHasMore(data.hasMore)
      setNextOffset(data.nextOffset)
    } catch (err) {
      console.error('[UserProfile] Load more failed:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  async function handleFollow(targetUsername) {
    const target = people.find(p => p.username === targetUsername)
    const wasFollowing = target?.isFollowedByMe ?? false
    const wasRequested = target?.isRequestedByMe ?? false
    const oldRequestId = target?.requestId ?? null

    try {
      const headers = await getHeaders()
      if (wasFollowing) {
        setPeople(prev => prev.map(p =>
          p.username === targetUsername ? { ...p, isFollowedByMe: false } : p
        ))
        await axios.delete(`/api/follows/${targetUsername}`, { headers })
      } else if (wasRequested) {
        // Cancel pending request via the withdraw endpoint.
        setPeople(prev => prev.map(p =>
          p.username === targetUsername ? { ...p, isRequestedByMe: false, requestId: null } : p
        ))
        if (oldRequestId) {
          await axios.delete(`/api/follow-requests/${oldRequestId}`, { headers })
        }
      } else {
        // Fresh follow — server decides follows vs follow_request based on
        // target.privacy_mode. Optimistically mark following; correct on
        // response if the server instead created a follow_request.
        setPeople(prev => prev.map(p =>
          p.username === targetUsername ? { ...p, isFollowedByMe: true } : p
        ))
        const res = await axios.post(`/api/follows/${targetUsername}`, {}, { headers })
        if (res.data?.requested) {
          setPeople(prev => prev.map(p =>
            p.username === targetUsername
              ? { ...p, isFollowedByMe: false, isRequestedByMe: true, requestId: res.data.requestId ?? null }
              : p
          ))
        }
      }
    } catch (err) {
      // 404 on the cancel-request branch means the follow_request is
      // already gone server-side (accepted, withdrawn from another tab,
      // or purged). Keep the optimistic clear — restoring wasRequested
      // would mislead the user into thinking the request still exists.
      const status = err?.response?.status
      if (wasRequested && !wasFollowing && status === 404) {
        // Optimistic clear stands.
      } else {
        setPeople(prev => prev.map(p =>
          p.username === targetUsername
            ? { ...p, isFollowedByMe: wasFollowing, isRequestedByMe: wasRequested, requestId: oldRequestId }
            : p
        ))
      }
      console.error('[follow] failed:', err?.response?.data ?? err?.message)
    }
  }

  const isFollowers = type === 'followers'
  const emptyText   = isFollowers ? 'No followers yet.' : 'Not following anyone yet.'
  const emptyIcon   = isFollowers ? '👥' : '🔭'

  if (loading) {
    return (
      <div className="follow-list">
        {[...Array(4)].map((_, i) => <div key={i} className="follow-list__skeleton" />)}
      </div>
    )
  }

  if (people.length === 0) {
    if (isPrivate) {
      return (
        <div className="profile-empty">
          <div className="profile-empty__icon">🔒</div>
          <p className="profile-empty__title">
            This user keeps their {isFollowers ? 'followers' : 'following'} list private.
          </p>
        </div>
      )
    }
    return (
      <div className="profile-empty">
        <div className="profile-empty__icon">{emptyIcon}</div>
        <p className="profile-empty__title">{emptyText}</p>
      </div>
    )
  }

  return (
    <div className="follow-list">
      {people.map(person => {
        const isFollowing = person.isFollowedByMe
        const isRequested = person.isRequestedByMe
        const isInactive = isFollowing || isRequested
        const isSelf = currentUser?.username === person.username
        return (
          <div key={person.username} className="follow-card">
            <Link to={`/user/${person.username}`} className="follow-card__left">
              <div className="follow-card__avatar" style={{ position: 'relative', background: person.avatarUrl ? undefined : person.avatarGrad }}>
                {person.avatarUrl
                  ? <img src={person.avatarUrl} alt={person.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  : (person.displayName[0] ?? '?').toUpperCase()}
                <OnlineDot userId={person.id} size={10} />
              </div>
              <div className="follow-card__info">
                <span className="follow-card__name">
                  {decodeEntities(person.displayName)}
                  {person.followsYouBack && (
                    <span
                      className="follow-card__mutual-badge"
                      style={{
                        fontSize: '0.7rem',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-full)',
                        background: 'var(--bg-faint, rgba(124, 58, 237, 0.1))',
                        color: 'var(--text-muted)',
                        marginLeft: 8,
                        fontWeight: 500,
                        display: 'inline-block',
                        verticalAlign: 'middle',
                      }}
                    >
                      Follows you
                    </span>
                  )}
                </span>
                <span className="follow-card__username">@{person.username}</span>
                {person.tagline && (
                  <span className="follow-card__tagline">{decodeEntities(person.tagline)}</span>
                )}
                {person.followers > 0 && (
                  <span className="follow-card__meta">{formatNum(person.followers)} followers</span>
                )}
              </div>
            </Link>
            {currentUser && !isSelf && (
              <button
                className={`btn follow-card__btn${isInactive ? ' btn-outline' : ' btn-primary'}`}
                onClick={() => handleFollow(person.username)}
              >
                {isFollowing ? '✓ Following' : isRequested ? '✓ Requested' : '+ Follow'}
              </button>
            )}
          </div>
        )
      })}
      {hasMore && people.length > 0 && (
        <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
          <button
            className="btn btn-outline"
            onClick={loadMore}
            disabled={loadingMore}
            style={{ fontSize: '0.9rem', padding: '10px 32px' }}
          >
            {loadingMore ? 'Loading…' : `Load more ${isFollowers ? 'followers' : 'following'}`}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Announcements tab ──────────────────────────────────────────────────────

function AnnouncementsTab({ username, isOwnProfile, currentUser }) {
  const [posts, setPosts]     = useState([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft]     = useState('')
  const [posting, setPosting] = useState(false)
  const [error, setError]     = useState(null)
  const [reportingId, setReportingId] = useState(null)

  useEffect(() => {
    setLoading(true)
    axios.get(`/api/announcements/${username}`)
      .then(r => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [username])

  async function handlePost(e) {
    e.preventDefault()
    const content = draft.trim()
    if (!content) return
    setPosting(true)
    setError(null)
    try {
      const headers = await getHeaders()
      const { data } = await axios.post('/api/announcements', { content }, { headers })
      setPosts(prev => [data, ...prev])
      setDraft('')
    } catch (err) {
      setError(err.response?.data?.error ?? 'Failed to post.')
    } finally {
      setPosting(false)
    }
  }

  const [editingId, setEditingId]     = useState(null)
  const [editText, setEditText]       = useState('')
  const [editSaving, setEditSaving]   = useState(false)

  function startEditing(post) {
    setEditingId(post.id)
    setEditText(post.content)
  }

  async function saveEdit() {
    if (!editText.trim() || editSaving) return
    setEditSaving(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.put(`/api/announcements/${editingId}`, { content: editText.trim() }, { headers })
      setPosts(prev => prev.map(p => p.id === editingId ? { ...p, content: data.content } : p))
      setEditingId(null)
    } catch (err) { console.error('[UserProfile] Failed to edit announcement:', err) } finally {
      setEditSaving(false)
    }
  }

  async function handleDelete(id) {
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/announcements/${id}`, { headers })
      setPosts(prev => prev.filter(p => p.id !== id))
    } catch (err) { console.error('[UserProfile] Failed to delete announcement:', err) }
  }

  function timeAgo(iso) {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1)   return 'just now'
    if (mins < 60)  return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24)   return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    if (days < 7)   return `${days}d ago`
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className="announcements-tab">
      {isOwnProfile && (
        <form className="announcement-composer" onSubmit={handlePost}>
          <textarea
            className="announcement-composer__input"
            placeholder="Share an update with your readers…"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            maxLength={2000}
            rows={3}
          />
          <p className="announcement-composer__disclaimer">
            Announcements notify all your followers. Limit: 1 per day. Misuse may result in loss of announcement privileges.
          </p>
          <div className="announcement-composer__footer">
            {error && <span className="announcement-composer__error">{error}</span>}
            <span className="announcement-composer__chars">{draft.length}/2000</span>
            <button className="btn btn-primary announcement-composer__btn" type="submit" disabled={posting || !draft.trim()}>
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="announcements-list">
          {[...Array(3)].map((_, i) => <div key={i} className="announcement-skeleton" />)}
        </div>
      ) : posts.length === 0 ? (
        <div className="profile-empty">
          <div className="profile-empty__icon">📣</div>
          <p className="profile-empty__title">No announcements yet.</p>
          {isOwnProfile && <p className="profile-empty__sub">Share updates, news, or notes with your readers.</p>}
        </div>
      ) : (
        <div className="announcements-list">
          {posts.map(post => (
            <div key={post.id} className="announcement-card">
              {currentUser && !isOwnProfile && (
                <button
                  className="announcement-card__report"
                  onClick={() => setReportingId(post.id)}
                  title="Report"
                  aria-label="Report"
                >⚑</button>
              )}
              {reportingId === post.id && (
                <ReportModal
                  contentType="announcement"
                  contentId={post.id}
                  onClose={() => setReportingId(null)}
                />
              )}
              {editingId === post.id ? (
                <div className="announcement-edit">
                  <textarea
                    className="announcement-composer__input"
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    autoFocus
                  />
                  <div className="announcement-edit__actions">
                    <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '5px 14px' }} onClick={saveEdit} disabled={editSaving || !editText.trim()}>
                      {editSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn" style={{ fontSize: '0.8rem', padding: '5px 14px' }} onClick={() => setEditingId(null)} disabled={editSaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="announcement-card__content">{decodeEntities(post.content)}</p>
                  <div className="announcement-card__footer">
                    <span className="announcement-card__time">{timeAgo(post.created_at)}</span>
                    {isOwnProfile && (
                      <div className="announcement-card__actions">
                        <button
                          className="announcement-card__edit"
                          onClick={() => startEditing(post)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          className="announcement-card__delete"
                          onClick={() => handleDelete(post.id)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Wall tab ────────────────────────────────────────────────────────────────

function WallTab({ username, isOwnProfile, isAdmin }) {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const focusCommentId = searchParams.get('comment')
  const focusPostIdParam = searchParams.get('post')
  const [posts, setPosts] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState(null)
  const [likedPosts, setLikedPosts] = useState(new Set())
  const [tombstone, setTombstone] = useState(null)
  const [focusPostId, setFocusPostId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.get(`/api/wall/posts/${username}?limit=20&offset=0`, { headers })
      setPosts(data.posts)
      setTotal(data.total)
      setLikedPosts(new Set((data.posts ?? []).filter(p => p.likedByMe).map(p => p.id)))
    } catch { /* empty */ }
    setLoading(false)
  }, [username])

  useEffect(() => { load() }, [load])

  // Deep-link to a specific comment via ?comment=uuid.
  useEffect(() => {
    if (!focusCommentId || loading) return
    let cancelled = false
    ;(async () => {
      try {
        const headers = await getHeaders()
        const { data } = await axios.get(`/api/wall/comments/${focusCommentId}/locate`, { headers })
        if (cancelled) return
        if (data.deleted) { setTombstone('This comment was removed.'); setFocusPostId(null); return }
        if (data.post?.deleted) { setTombstone('The wall post has been removed.'); setFocusPostId(null); return }
        setTombstone(null)
        const existing = posts.find(p => p.id === data.post_id)
        if (existing) { setFocusPostId(data.post_id); return }
        // Fetch the target post and prepend to the feed so it can be scrolled to.
        try {
          const { data: post } = await axios.get(`/api/wall/posts/by-id/${data.post_id}`, { headers })
          if (cancelled || !post) return
          setPosts(prev => prev.some(p => p.id === post.id) ? prev : [post, ...prev])
          if (post.likedByMe) setLikedPosts(prev => new Set(prev).add(post.id))
          setFocusPostId(post.id)
        } catch (err) {
          if (err.response?.status === 410) setTombstone('The wall post has been removed.')
        }
      } catch (err) {
        if (err.response?.status === 404) setTombstone('Linked comment is unavailable.')
      }
    })()
    return () => { cancelled = true }
  }, [focusCommentId, loading, posts])

  // Deep-link to a specific post via ?post=uuid (no comment).
  useEffect(() => {
    if (!focusPostIdParam || focusCommentId || loading) return
    let cancelled = false
    ;(async () => {
      const existing = posts.find(p => p.id === focusPostIdParam)
      if (existing) { setTombstone(null); setFocusPostId(focusPostIdParam); return }
      try {
        const headers = await getHeaders()
        const { data: post } = await axios.get(`/api/wall/posts/by-id/${focusPostIdParam}`, { headers })
        if (cancelled || !post) return
        setTombstone(null)
        setPosts(prev => prev.some(p => p.id === post.id) ? prev : [post, ...prev])
        if (post.likedByMe) setLikedPosts(prev => new Set(prev).add(post.id))
        setFocusPostId(post.id)
      } catch (err) {
        if (cancelled) return
        if (err.response?.status === 410) setTombstone('The wall post has been removed.')
        else if (err.response?.status === 404) setTombstone('Linked post is unavailable.')
        setFocusPostId(null)
      }
    })()
    return () => { cancelled = true }
  }, [focusPostIdParam, focusCommentId, loading, posts])

  // Scroll the focused post into view + flash, once it's in the DOM.
  useEffect(() => {
    if (!focusPostId) return
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`wall-post-${focusPostId}`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    return () => cancelAnimationFrame(raf)
  }, [focusPostId])

  function dismissTombstone() {
    setTombstone(null)
    const next = new URLSearchParams(searchParams)
    next.delete('comment')
    next.delete('post')
    setSearchParams(next, { replace: true })
  }

  async function handlePost(e) {
    e.preventDefault()
    const content = draft.trim()
    if (!content) return
    setPosting(true)
    setError(null)
    try {
      const headers = await getHeaders()
      const { data } = await axios.post('/api/wall/posts', { content }, { headers })
      setPosts(prev => [data, ...prev])
      setTotal(prev => prev + 1)
      setDraft('')
    } catch (err) {
      setError(err.response?.data?.error ?? 'Failed to post.')
    } finally { setPosting(false) }
  }

  async function handleLoadMore() {
    setLoadingMore(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.get(`/api/wall/posts/${username}?limit=20&offset=${posts.length}`, { headers })
      setPosts(prev => [...prev, ...data.posts])
      setTotal(data.total)
      setLikedPosts(prev => { const s = new Set(prev); (data.posts ?? []).filter(p => p.likedByMe).forEach(p => s.add(p.id)); return s })
    } catch { /* empty */ }
    setLoadingMore(false)
  }

  async function handleDeletePost(postId) {
    if (!window.confirm('Delete this post?')) return
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/wall/posts/${postId}`, { headers })
      setPosts(prev => prev.filter(p => p.id !== postId))
      setTotal(prev => prev - 1)
    } catch { /* empty */ }
  }

  async function handleToggleLike(postId) {
    const wasLiked = likedPosts.has(postId)
    setLikedPosts(prev => { const s = new Set(prev); wasLiked ? s.delete(postId) : s.add(postId); return s })
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: p.likes_count + (wasLiked ? -1 : 1) } : p))
    try {
      const headers = await getHeaders()
      const { data } = await axios.post(`/api/wall/posts/${postId}/like`, {}, { headers })
      setLikedPosts(prev => { const s = new Set(prev); data.liked ? s.add(postId) : s.delete(postId); return s })
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: data.likes_count } : p))
    } catch {
      setLikedPosts(prev => { const s = new Set(prev); wasLiked ? s.add(postId) : s.delete(postId); return s })
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: p.likes_count + (wasLiked ? 1 : -1) } : p))
    }
  }

  function handleCommentsCountChange(postId, delta) {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: Math.max(0, p.comments_count + delta) } : p))
  }

  return (
    <div className="wall-tab">
      {tombstone && (
        <div className="wall-tombstone" role="status">
          <span>{tombstone}</span>
          <button className="wall-tombstone__dismiss" onClick={dismissTombstone} aria-label="Dismiss">×</button>
        </div>
      )}
      {isOwnProfile && (
        <form className="wall-composer" onSubmit={handlePost}>
          <textarea
            className="wall-composer__input"
            placeholder="Write something on your wall…"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            maxLength={5000}
            rows={3}
          />
          <div className="wall-composer__footer">
            {error && <span className="wall-composer__error">{error}</span>}
            <span className="wall-composer__chars">{draft.length}/5000</span>
            <button className="btn btn-primary" type="submit" disabled={posting || !draft.trim()}>
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="wall-list">{[...Array(3)].map((_, i) => <div key={i} className="wall-skeleton" />)}</div>
      ) : posts.length === 0 ? (
        <div className="profile-empty">
          <div className="profile-empty__icon">📝</div>
          <p className="profile-empty__title">No posts yet</p>
          <p className="profile-empty__desc">
            {isOwnProfile ? 'Write something on your wall to get started.' : `${username} hasn't posted anything yet.`}
          </p>
        </div>
      ) : (
        <>
          <div className="wall-list">
            {posts.map(post => (
              <WallPost
                key={post.id}
                post={post}
                liked={likedPosts.has(post.id)}
                onToggleLike={() => handleToggleLike(post.id)}
                onDelete={() => handleDeletePost(post.id)}
                onEdit={(id, updated) => setPosts(prev => prev.map(p => p.id === id ? { ...p, ...updated } : p))}
                canDelete={isOwnProfile || isAdmin}
                user={user}
                isAdmin={isAdmin}
                onCommentsCountChange={delta => handleCommentsCountChange(post.id, delta)}
                focusCommentId={post.id === focusPostId ? focusCommentId : null}
                focused={!focusCommentId && post.id === focusPostId}
              />
            ))}
          </div>
          {posts.length < total && (
            <div className="wall-load-more">
              <button className="btn btn-outline wall-load-more__btn" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}



function AboutStat({ label, value }) {
  return (
    <div className="about-stat-row">
      <span className="about-stat-label">{label}</span>
      <span className="about-stat-value">{value}</span>
    </div>
  )
}

// ─── Admin Warning Tool ───────────────────────────────────────────────────────

const WARNING_LEVELS = [
  { value: 'mild',     label: 'Mild',     color: '#EAB308' },
  { value: 'moderate', label: 'Moderate', color: '#F97316' },
  { value: 'severe',   label: 'Severe',   color: '#DC2626' },
]

function AdminWarningTool({ userId, onIssued }) {
  const [open, setOpen]               = useState(false)
  const [level, setLevel]             = useState('mild')
  const [explanation, setExplanation]  = useState('')
  const [sending, setSending]          = useState(false)
  const [error, setError]              = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!explanation.trim() || sending) return
    setSending(true)
    setError(null)
    try {
      const headers = await getHeaders()
      const { data } = await axios.post('/api/admin/warnings', {
        userId,
        level,
        explanation: explanation.trim(),
      }, { headers })
      onIssued({ id: data.id, level: data.level, explanation: data.explanation, createdAt: data.created_at })
      setExplanation('')
      setLevel('mild')
      setOpen(false)
    } catch (err) {
      setError(err.response?.data?.error ?? 'Failed to issue warning')
    } finally {
      setSending(false)
    }
  }

  if (!open) {
    return (
      <button className="btn btn-outline" style={{ fontSize: '0.8rem', color: '#EAB308', borderColor: '#EAB308' }} onClick={() => setOpen(true)}>
        Issue Warning
      </button>
    )
  }

  return (
    <form className="admin-warning-form" onSubmit={handleSubmit}>
      <div className="admin-warning-form__header">
        <strong>Issue Account Warning</strong>
        <button type="button" className="admin-warning-form__close" onClick={() => setOpen(false)}>&times;</button>
      </div>
      <div className="admin-warning-form__levels">
        {WARNING_LEVELS.map(l => (
          <button
            key={l.value}
            type="button"
            className={`admin-warning-form__level${level === l.value ? ' admin-warning-form__level--active' : ''}`}
            style={{ '--warn-color': l.color }}
            onClick={() => setLevel(l.value)}
          >
            {l.label}
          </button>
        ))}
      </div>
      <textarea
        className="admin-warning-form__text"
        placeholder="Explanation shown on the user's profile…"
        value={explanation}
        onChange={e => setExplanation(e.target.value)}
        maxLength={500}
        rows={3}
      />
      {error && <p className="admin-warning-form__error">{error}</p>}
      <div className="admin-warning-form__footer">
        <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>{explanation.length}/500</span>
        <button className="btn btn-primary" type="submit" disabled={!explanation.trim() || sending}>
          {sending ? 'Issuing…' : 'Issue Warning'}
        </button>
      </div>
    </form>
  )
}

// ─── Admin Suspend Tool ──────────────────────────────────────────────────────

const SUSPEND_DURATIONS = [
  { value: '1h',  label: '1 hour' },
  { value: '6h',  label: '6 hours' },
  { value: '12h', label: '12 hours' },
  { value: '1d',  label: '1 day' },
  { value: '3d',  label: '3 days' },
  { value: '7d',  label: '7 days' },
  { value: '14d', label: '14 days' },
  { value: '30d', label: '30 days' },
]

function AdminSuspendTool({ userId }) {
  const [open, setOpen]         = useState(false)
  const [duration, setDuration] = useState('1d')
  const [reason, setReason]     = useState('')
  const [sending, setSending]   = useState(false)
  const [error, setError]       = useState(null)
  const [success, setSuccess]   = useState(false)
  const [activeSuspensions, setActiveSuspensions] = useState([])
  const [loadingSuspensions, setLoadingSuspensions] = useState(false)

  async function loadSuspensions() {
    setLoadingSuspensions(true)
    try {
      const headers = await getHeaders()
      const { data } = await axios.get(`/api/admin/suspensions/${userId}`, { headers })
      setActiveSuspensions(data ?? [])
    } catch (err) { console.error('[UserProfile] Failed to load suspensions:', err) }
    setLoadingSuspensions(false)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!reason.trim() || sending) return
    setSending(true)
    setError(null)
    try {
      const headers = await getHeaders()
      await axios.post('/api/admin/suspensions', { userId, reason: reason.trim(), duration }, { headers })
      setReason('')
      setDuration('1d')
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      loadSuspensions()
    } catch (err) {
      setError(err.response?.data?.error ?? 'Failed to suspend user')
    } finally {
      setSending(false)
    }
  }

  async function handleLift(id) {
    try {
      const headers = await getHeaders()
      await axios.delete(`/api/admin/suspensions/${id}`, { headers })
      setActiveSuspensions(prev => prev.filter(s => s.id !== id))
    } catch (err) { console.error('[UserProfile] Failed to lift suspension:', err) }
  }

  if (!open) {
    return (
      <button className="btn btn-outline" style={{ fontSize: '0.8rem', color: '#EF4444', borderColor: '#EF4444' }} onClick={() => { setOpen(true); loadSuspensions() }}>
        Suspend
      </button>
    )
  }

  return (
    <form className="admin-warning-form" onSubmit={handleSubmit} style={{ borderColor: '#EF4444' }}>
      <div className="admin-warning-form__header">
        <strong style={{ color: '#EF4444' }}>Suspend Account</strong>
        <button type="button" className="admin-warning-form__close" onClick={() => setOpen(false)}>&times;</button>
      </div>

      {activeSuspensions.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-faint)', marginBottom: 6 }}>Active suspensions</div>
          {activeSuspensions.map(s => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', padding: '6px 0', borderBottom: '1px solid var(--border-faint)' }}>
              <div>
                <div style={{ color: 'var(--text)' }}>{s.reason}</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '0.7rem' }}>Expires: {new Date(s.expires_at).toLocaleString()}</div>
              </div>
              <button type="button" className="btn btn-outline" style={{ fontSize: '0.7rem', padding: '2px 8px', color: '#059669', borderColor: '#059669' }} onClick={() => handleLift(s.id)}>
                Lift
              </button>
            </div>
          ))}
        </div>
      )}
      {loadingSuspensions && <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginBottom: 8 }}>Loading…</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {SUSPEND_DURATIONS.map(d => (
          <button
            key={d.value}
            type="button"
            style={{
              padding: '4px 10px',
              fontSize: '0.72rem',
              fontWeight: duration === d.value ? 700 : 400,
              borderRadius: 6,
              border: `1px solid ${duration === d.value ? '#EF4444' : 'var(--border)'}`,
              background: duration === d.value ? '#EF4444' : 'transparent',
              color: duration === d.value ? '#fff' : 'var(--text-muted)',
              cursor: 'pointer',
            }}
            onClick={() => setDuration(d.value)}
          >
            {d.label}
          </button>
        ))}
      </div>

      <textarea
        className="admin-warning-form__text"
        placeholder="Reason for suspension (shown to the user)…"
        value={reason}
        onChange={e => setReason(e.target.value)}
        maxLength={500}
        rows={3}
      />
      {error && <p className="admin-warning-form__error">{error}</p>}
      {success && <p style={{ fontSize: '0.8rem', color: '#059669', margin: '4px 0' }}>User suspended.</p>}
      <div className="admin-warning-form__footer">
        <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>{reason.length}/500</span>
        <button className="btn btn-primary" type="submit" disabled={!reason.trim() || sending} style={{ background: '#EF4444' }}>
          {sending ? 'Suspending…' : 'Suspend'}
        </button>
      </div>
    </form>
  )
}

// ─── Admin Restrict Tool ──────────────────────────────────────────────────────

function AdminRestrictTool({ userId, restricted, onToggle }) {
  const [loading, setLoading] = useState(false)

  async function toggle() {
    const action = restricted ? 'unrestrict' : 'restrict'
    if (!window.confirm(`${restricted ? 'Unrestrict' : 'Restrict'} this user? ${restricted ? 'They will regain full access.' : 'They will be in view-only mode.'}`)) return
    setLoading(true)
    try {
      const headers = await getHeaders()
      await axios.patch(`/api/admin/users/${userId}/restricted`, { restricted: !restricted }, { headers })
      onToggle(!restricted)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      className="btn btn-outline"
      style={{ fontSize: '0.8rem', color: restricted ? '#059669' : '#f59e0b', borderColor: restricted ? '#059669' : '#f59e0b' }}
      onClick={toggle}
      disabled={loading}
    >
      {loading ? '…' : restricted ? 'Unrestrict' : 'Restrict'}
    </button>
  )
}

// ─── Admin Hide Button ────────────────────────────────────────────────────────

function AdminHideButton({ type, id, hidden, onToggle }) {
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(0) // 0 = idle, 1 = first click, 2 = second click (proceed)

  async function handleClick() {
    if (confirm === 0) {
      setConfirm(1)
      return
    }
    if (confirm === 1) {
      setConfirm(2)
      setBusy(true)
      try {
        const headers = await getHeaders()
        await axios.patch(`/api/admin/hide/${type}/${id}`, { hidden: !hidden }, { headers })
        onToggle(!hidden)
      } catch (err) { console.error('[UserProfile] Failed to toggle content visibility:', err) }
      setBusy(false)
      setConfirm(0)
    }
  }

  const label = busy
    ? '…'
    : confirm === 1
      ? `Are you sure?`
      : hidden ? `Unhide ${type}` : `Hide ${type}`

  const color = confirm === 1
    ? '#DC2626'
    : hidden ? '#16A34A' : 'var(--text-faint, #888)'
  const border = confirm === 1
    ? '#DC2626'
    : hidden ? '#16A34A' : 'var(--border, #888)'

  return (
    <button
      className="btn btn-outline"
      style={{ fontSize: '0.8rem', color, borderColor: border }}
      disabled={busy}
      onClick={handleClick}
      onBlur={() => setConfirm(0)}
    >
      {label}
    </button>
  )
}
